import { Container, Skeleton } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useActiveSimulation } from '../../active-simulation/useActiveSimulation';
import { useAssessments, useProfile, useSimulation } from '../../api';
import AppBar from '../../app-bar/AppBar';
import Assessments from '../../assessments/Assessments';
import { useCheckRole } from '../../auth';
import BackLink from '../../back-link/BackLink';
import Breadcrumbs from '../../breadcrumbs/Breadcrumbs';
import KSimHeader from '../../k-sim-header/KSimHeader';
import { apiPaths, queryClient } from '../../queryClient';
import { useSignalREffect } from '../../signalr/SignalRContext';
import SimulationCard from '../../simulation-card/SimulationCard';
import SimulationSkeleton from '../../skeletons/SimulationSkeleton';
import {
  AssessmentUpdatedNotification,
  Assessment as IAssessment,
  SimulationEventMessage,
  TechnologyPlatform,
  TechnologyPlatformNumber,
} from '../../types';
import { Pages } from '../../types/pages';
import { isRunning } from '../../utils/activeSimulation';
import { parseTime } from '../../utils/parseTime';
import { useActiveCompany } from '../../utils/useCompany';

/**
 * Simulation detail page from the student's point of view
 */
const Simulation = () => {
  useCheckRole(['Student', 'Instructor']);
  const { exerciseId, simulationId } = useParams();

  const activeCompany = useActiveCompany();
  const { profile } = useProfile();
  const { activeSimulation } = useActiveSimulation();
  // While SignalR is already pushing live updates for this simulation (via the active-simulation
  // state), we don't need to also poll for it - poll only as a fallback when that's not the case.
  const hasLiveUpdates = !!(activeSimulation && activeSimulation.id === simulationId && isRunning(activeSimulation));
  const { simulation, simulationError, simulationLoading } = useSimulation(exerciseId, simulationId, hasLiveUpdates);
  const technologyPlatform = simulation?.simulator?.platform;

  const { assessments, assessmentsError, assessmentsLoading } = useAssessments(
    exerciseId,
    simulationId,
    technologyPlatform,
    simulation?.assessmentVisibility,
  );
  const [assessmentsState, setAssessments] = useState<IAssessment[]>([]);
  useEffect(() => {
    assessments && setAssessments(assessments);
  }, [assessments]);

  useSignalREffect(
    SimulationEventMessage.AssessmentUpdated,
    (...args) => {
      const { assessments, userId } = args[0] as AssessmentUpdatedNotification;
      return (
        profile?.userId === userId &&
        technologyPlatform === TechnologyPlatformNumber[TechnologyPlatform.Spirit] &&
        simulation?.assessmentVisibility &&
        setAssessments(assessments)
      );
    },
    [profile, simulation],
  );

  useSignalREffect(
    SimulationEventMessage.SimulationCompleted,
    () => {
      queryClient
        .fetchQuery([
          {
            path: apiPaths.learn.simulation(activeCompany?.companyId, simulation?.exercise.id, simulation?.id),
          },
        ])
        .then(() => {
          queryClient.fetchQuery([
            {
              path: apiPaths.learn.assessment(activeCompany?.companyId, simulation?.id, technologyPlatform),
            },
          ]);
        });
    },
    [activeCompany, simulation],
  );

  if (simulationLoading) {
    return (
      <SimulationSkeleton
        breadcrumbs={[
          { label: 'Home', to: Pages.Learn },
          { to: '..', label: <Skeleton width={160} /> },
          { label: <Skeleton width={120} /> },
        ]}
      />
    );
  }

  if (simulationError) {
    throw simulationError;
  }

  const {
    endTime,
    exercise: { adHoc, name: exerciseName, type: exerciseType },
    name,
    position,
    startTime,
    status,
    simulator: { type: simulatorType },
  } = parseTime(simulation);

  return (
    <>
      <AppBar title="Exercise" />
      <Breadcrumbs
        items={[{ label: 'Home', to: '/' }, { to: '..', label: exerciseName }, { label: name }]}
        sx={{ display: { xs: 'none', sm: 'flex' } }}
      />
      <Container>
        <BackLink label="Back to exercise" sx={{ display: { xs: 'inline-block', sm: 'none' }, my: 2 }} to=".." />
        <SimulationCard
          adHoc={adHoc}
          endTime={endTime}
          exerciseName={exerciseName}
          exerciseType={exerciseType}
          position={position}
          status={status}
          startTime={startTime}
          simulatorType={simulatorType}
        />
        <KSimHeader
          exerciseType={exerciseType}
          simulatorType={simulatorType}
          sx={{ display: { xs: 'grid', sm: 'none' }, mt: 1, mb: { xs: 2, sm: 4 } }}
        />
        {simulation?.assessmentVisibility && (
          <Assessments
            assessments={assessmentsState}
            loading={assessmentsLoading}
            error={assessmentsError}
            simulationStatus={simulation.status}
          />
        )}
      </Container>
    </>
  );
};

export default Simulation;
